import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { RONDAS_ORDEN, RONDA_LABELS_DEFAULT } from '../../../components/torneos/TorneoBracket';

const slugify = (value) => (value || 'torneo')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 40) || 'torneo';

const TBD = 'A confirmar';
const HEAD_COLOR = [24, 144, 255];
const WINNER_FILL = [232, 255, 176];
const WINNER_TEXT = [40, 90, 0];
const LOSER_TEXT = [120, 120, 120];
const TBD_TEXT = [150, 150, 150];
const ALT_FILL = [248, 249, 250];
const LINE = [160, 160, 160];

const getTopPosition = (roundIndex, matchIndex, cardHeight, gap) => {
  const unit = cardHeight + gap;
  const multiplier = 2 ** roundIndex;
  return matchIndex * multiplier * unit + ((multiplier - 1) * unit) / 2;
};

const getBracketHeight = (firstRoundCount, cardHeight, gap) => {
  if (firstRoundCount <= 0) return cardHeight;
  return getTopPosition(0, firstRoundCount - 1, cardHeight, gap) + cardHeight;
};

const truncate = (doc, text, maxWidth) => {
  const value = String(text || '');
  if (!value || doc.getTextWidth(value) <= maxWidth) return value;
  let cut = value;
  while (cut.length > 1 && doc.getTextWidth(`${cut}...`) > maxWidth) {
    cut = cut.slice(0, -1);
  }
  return `${cut}...`;
};

const normalizePorRonda = (porRonda) => {
  if (!porRonda) return {};
  const result = {};
  for (const [ronda, value] of Object.entries(porRonda)) {
    result[ronda] = Array.isArray(value) ? value : (value.partidos || []);
  }
  return result;
};

const normalizeMatch = (raw, ronda) => {
  const parejaA = raw.pareja_a ?? null;
  const parejaB = raw.pareja_b ?? null;
  const estado = raw.tpl_estado ?? (raw.resultado ? 'JUGADO' : 'PENDIENTE');
  const ganadorId = raw.tpl_ganador_inscripcion_id ?? null;
  const inscripcionAId = raw.tpl_inscripcion_a_id ?? null;
  const inscripcionBId = raw.tpl_inscripcion_b_id ?? null;
  const ganadorLabel = raw.ganador ?? null;

  let winnerIsA = false;
  let winnerIsB = false;
  if (estado === 'JUGADO' || estado === 'WO') {
    if (ganadorId && inscripcionAId) {
      winnerIsA = ganadorId === inscripcionAId;
      winnerIsB = ganadorId === inscripcionBId;
    } else if (ganadorLabel) {
      winnerIsA = ganadorLabel === parejaA;
      winnerIsB = ganadorLabel === parejaB;
    }
  }

  return {
    ronda,
    orden: raw.tpl_orden ?? raw.orden ?? 0,
    parejaA: parejaA || TBD,
    parejaB: parejaB || TBD,
    isPlaceholderA: !parejaA,
    isPlaceholderB: !parejaB,
    estado,
    setsA: raw.tpl_sets_a ?? null,
    setsB: raw.tpl_sets_b ?? null,
    resultado: raw.resultado_formateado ?? raw.resultado ?? null,
    ganadorLabel,
    winnerIsA,
    winnerIsB,
    clasificacionA: raw.clasificacion_a ?? null,
    clasificacionB: raw.clasificacion_b ?? null,
  };
};

const buildRounds = (porRonda) => {
  const flat = normalizePorRonda(porRonda);
  const rounds = RONDAS_ORDEN.filter((r) => (flat[r]?.length ?? 0) > 0);
  const normalized = rounds.map((ronda) =>
    (flat[ronda] || [])
      .slice()
      .sort((a, b) => (a.tpl_orden ?? a.orden ?? 0) - (b.tpl_orden ?? b.orden ?? 0))
      .map((m) => normalizeMatch(m, ronda))
  );

  let champion = null;
  const finalRound = normalized[normalized.length - 1];
  if (finalRound?.length === 1) {
    const final = finalRound[0];
    if (final.estado === 'JUGADO' || final.estado === 'WO') {
      champion = final.ganadorLabel
        || (final.winnerIsA ? final.parejaA : final.winnerIsB ? final.parejaB : null);
    }
  }

  return {
    rounds,
    normalized,
    firstRoundCount: normalized[0]?.length ?? 1,
    champion,
  };
};

const formatearResultadoLlave = (partido) => {
  if (partido.tpl_estado !== 'JUGADO' && partido.tpl_estado !== 'WO') return 'Pendiente';
  const base = partido.resultado_formateado
    || `${partido.tpl_sets_a ?? 0} - ${partido.tpl_sets_b ?? 0}`;
  return partido.tpl_estado === 'WO' ? `${base} WO` : base;
};

const addFooters = (doc, margin, generado) => {
  const pageCount = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(`Generado ${generado}`, margin, pageH - 8);
    doc.text(`Página ${p} de ${pageCount}`, pageW - margin, pageH - 8, { align: 'right' });
  }
};

const drawPageHeader = (doc, {
  margin,
  pageW,
  nombreTorneo,
  fechaTorneo,
  subtitle,
  isFirstPage,
  extraRight,
}) => {
  const y = margin;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(isFirstPage ? 16 : 11);
  doc.setTextColor(20, 20, 20);
  doc.text(nombreTorneo, margin, y + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 90, 90);
  doc.text(isFirstPage ? subtitle : `${subtitle} (cont.)`, margin, y + 11);

  const meta = [
    fechaTorneo ? `Fecha: ${fechaTorneo}` : null,
    extraRight,
  ].filter(Boolean).join('   ·   ');
  if (meta) {
    doc.setFontSize(8.5);
    doc.text(meta, pageW - margin, y + 5, { align: 'right' });
  }

  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, y + 14, pageW - margin, y + 14);
  return y + 18;
};

const drawTeamRow = (doc, {
  x, y, w, h, name, badge, score, isWinner, isLoser, isTbd, showWo, fontSize,
}) => {
  if (isWinner) {
    doc.setFillColor(...WINNER_FILL);
    doc.rect(x, y, w, h, 'F');
  }

  const scoreW = score != null ? doc.getTextWidth(String(score)) + 3 : 0;
  const woW = showWo ? 10 : 0;
  const badgeW = badge ? Math.min(14, doc.getTextWidth(String(badge)) + 4) : 0;
  const nameW = w - 4 - scoreW - woW - badgeW - (badge ? 2 : 0);

  doc.setFont('helvetica', isWinner ? 'bold' : (isTbd ? 'italic' : 'normal'));
  doc.setFontSize(fontSize);
  if (isWinner) doc.setTextColor(...WINNER_TEXT);
  else if (isTbd) doc.setTextColor(...TBD_TEXT);
  else if (isLoser) doc.setTextColor(...LOSER_TEXT);
  else doc.setTextColor(33, 33, 33);

  doc.text(truncate(doc, name, nameW), x + 2, y + h * 0.68);

  let rightX = x + w - 2;
  if (score != null) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(fontSize + 0.5);
    doc.setTextColor(isWinner ? WINNER_TEXT[0] : 60, isWinner ? WINNER_TEXT[1] : 60, isWinner ? WINNER_TEXT[2] : 60);
    doc.text(String(score), rightX, y + h * 0.68, { align: 'right' });
    rightX -= scoreW;
  }
  if (showWo) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(Math.max(6, fontSize - 1));
    doc.setTextColor(210, 105, 30);
    doc.text('WO', rightX, y + h * 0.68, { align: 'right' });
    rightX -= woW;
  }
  if (badge) {
    const bw = badgeW;
    const bh = Math.min(h - 1.2, Math.max(3.6, fontSize * 0.75));
    const bx = rightX - bw;
    const by = y + (h - bh) / 2;
    doc.setFillColor(230, 230, 230);
    doc.roundedRect(bx, by, bw, bh, 0.8, 0.8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(Math.max(6, fontSize - 1));
    doc.setTextColor(80, 80, 80);
    doc.text(String(badge), bx + bw / 2, by + bh * 0.72, { align: 'center' });
  }
};

const exportarLlaveArbolPdf = ({
  torneo,
  porRonda,
  rondaLabels,
  torneoFinalizado,
}) => {
  const { rounds, normalized, firstRoundCount, champion } = buildRounds(porRonda);
  if (!rounds.length) {
    throw new Error('NO_LLAVE');
  }

  const format = firstRoundCount > 4 ? 'a3' : 'a4';
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const footerReserve = 12;
  const nombreTorneo = torneo?.tor_nombre || 'Torneo';
  const fechaTorneo = torneo?.tor_fecha ? dayjs(torneo.tor_fecha).format('DD/MM/YYYY') : null;

  const headerBottom = drawPageHeader(doc, {
    margin,
    pageW,
    nombreTorneo,
    fechaTorneo,
    subtitle: 'Llave eliminatoria — Árbol',
    isFirstPage: true,
    extraRight: torneoFinalizado ? 'Estado: finalizado' : 'Estado: en curso',
  });

  const hasResult = normalized.some((round) => round.some((m) => m.resultado));
  const baseCardW = 58;
  const baseTeamH = 7.2;
  const baseResultH = hasResult ? 4.2 : 0;
  const baseCardH = baseTeamH * 2 + baseResultH + 0.8;
  const baseGap = Math.max(5, baseCardH * 0.32);
  const baseRoundGap = 16;
  const champW = champion ? 42 : 0;

  const contentW = rounds.length * baseCardW + (rounds.length - 1) * baseRoundGap + champW;
  const contentH = getBracketHeight(firstRoundCount, baseCardH, baseGap) + 10;
  const availW = pageW - margin * 2;
  const availH = pageH - headerBottom - footerReserve - 8;
  const scale = Math.min(availW / contentW, availH / contentH, 1);

  const cardW = baseCardW * scale;
  const teamH = baseTeamH * scale;
  const resultH = baseResultH * scale;
  const cardH = baseCardH * scale;
  const gap = baseGap * scale;
  const roundGap = baseRoundGap * scale;
  const fontSize = Math.max(6.2, 8 * scale);
  const offsetX = margin + Math.max(0, (availW - contentW * scale) / 2);
  const offsetY = headerBottom + 2;

  rounds.forEach((ronda, roundIndex) => {
    const x = offsetX + roundIndex * (cardW + roundGap);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(Math.max(7, 9 * scale));
    doc.setTextColor(110, 110, 110);
    const title = (rondaLabels[ronda] || RONDA_LABELS_DEFAULT[ronda] || ronda).toUpperCase();
    doc.text(title, x + cardW / 2, offsetY + 4, { align: 'center' });
  });

  const matchesTop = offsetY + 8;
  const layouts = normalized.map((matches, roundIndex) =>
    matches.map((_, matchIndex) => {
      const x = offsetX + roundIndex * (cardW + roundGap);
      const y = matchesTop + getTopPosition(roundIndex, matchIndex, cardH, gap);
      return { x, y, right: x + cardW, left: x, centerY: y + cardH / 2 };
    })
  );

  doc.setDrawColor(...LINE);
  doc.setLineWidth(Math.max(0.25, 0.4 * scale));
  for (let r = 0; r < layouts.length - 1; r += 1) {
    layouts[r].forEach((from, m) => {
      const to = layouts[r + 1][Math.floor(m / 2)];
      if (!from || !to) return;
      const midX = from.right + (to.left - from.right) / 2;
      doc.line(from.right, from.centerY, midX, from.centerY);
      doc.line(midX, from.centerY, midX, to.centerY);
      doc.line(midX, to.centerY, to.left, to.centerY);
    });
  }

  normalized.forEach((matches, roundIndex) => {
    matches.forEach((match, matchIndex) => {
      const pos = layouts[roundIndex][matchIndex];
      const played = match.estado === 'JUGADO' || match.estado === 'WO';
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.3);
      doc.roundedRect(pos.x, pos.y, cardW, cardH, 1.2 * scale, 1.2 * scale, 'FD');

      drawTeamRow(doc, {
        x: pos.x,
        y: pos.y,
        w: cardW,
        h: teamH,
        name: match.parejaA,
        badge: match.clasificacionA,
        score: played ? match.setsA : null,
        isWinner: match.winnerIsA,
        isLoser: played && !match.winnerIsA,
        isTbd: match.isPlaceholderA,
        showWo: false,
        fontSize,
      });

      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.2);
      doc.line(pos.x, pos.y + teamH, pos.x + cardW, pos.y + teamH);

      drawTeamRow(doc, {
        x: pos.x,
        y: pos.y + teamH,
        w: cardW,
        h: teamH,
        name: match.parejaB,
        badge: match.clasificacionB,
        score: played ? match.setsB : null,
        isWinner: match.winnerIsB,
        isLoser: played && !match.winnerIsB,
        isTbd: match.isPlaceholderB,
        showWo: match.estado === 'WO',
        fontSize,
      });

      if (resultH && match.resultado) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(Math.max(5.8, fontSize - 1));
        doc.setTextColor(110, 110, 110);
        doc.text(
          truncate(doc, match.resultado, cardW - 4),
          pos.x + 2,
          pos.y + teamH * 2 + resultH * 0.72
        );
      }
    });
  });

  if (champion) {
    const last = layouts[layouts.length - 1]?.[0];
    const x = offsetX + rounds.length * (cardW + roundGap) - roundGap + 4 * scale;
    const boxH = 22 * scale;
    const boxW = 40 * scale;
    const y = (last?.centerY ?? matchesTop + contentH * scale / 2) - boxH / 2;
    doc.setFillColor(247, 255, 224);
    doc.setDrawColor(160, 200, 40);
    doc.setLineWidth(0.5);
    doc.roundedRect(x, y, boxW, boxH, 1.6 * scale, 1.6 * scale, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(Math.max(6.5, 8 * scale));
    doc.setTextColor(90, 130, 0);
    doc.text('CAMPEÓN', x + boxW / 2, y + 6 * scale, { align: 'center' });
    doc.setFontSize(Math.max(6.2, 7.2 * scale));
    doc.setTextColor(...WINNER_TEXT);
    const champLines = doc.splitTextToSize(champion, boxW - 4 * scale).slice(0, 2);
    doc.text(champLines, x + boxW / 2, y + 12 * scale, { align: 'center' });
  }

  addFooters(doc, margin, dayjs().format('DD/MM/YYYY HH:mm'));
  doc.save(`llave-arbol-${slugify(nombreTorneo)}-${dayjs().format('YYYY-MM-DD')}.pdf`);
};

const exportarLlaveTablaPdf = ({
  torneo,
  porRonda,
  rondaLabels,
  torneoFinalizado,
}) => {
  const flat = normalizePorRonda(porRonda);
  const rounds = RONDAS_ORDEN.filter((r) => (flat[r]?.length ?? 0) > 0);
  if (!rounds.length) {
    throw new Error('NO_LLAVE');
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const footerReserve = 14;
  const nombreTorneo = torneo?.tor_nombre || 'Torneo';
  const fechaTorneo = torneo?.tor_fecha ? dayjs(torneo.tor_fecha).format('DD/MM/YYYY') : null;

  let y = drawPageHeader(doc, {
    margin,
    pageW,
    nombreTorneo,
    fechaTorneo,
    subtitle: 'Llave eliminatoria — Tabla',
    isFirstPage: true,
    extraRight: torneoFinalizado ? 'Estado: finalizado' : 'Estado: en curso',
  });

  const { champion } = buildRounds(porRonda);
  if (champion) {
    doc.setFillColor(247, 255, 224);
    doc.setDrawColor(160, 200, 40);
    doc.setLineWidth(0.4);
    doc.roundedRect(margin, y, pageW - margin * 2, 12, 1.6, 1.6, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(90, 130, 0);
    doc.text('Campeón', margin + 4, y + 5);
    doc.setFontSize(10);
    doc.setTextColor(...WINNER_TEXT);
    doc.text(champion, margin + 4, y + 10);
    y += 16;
  }

  rounds.forEach((ronda, idx) => {
    if (y > pageH - footerReserve - 32) {
      doc.addPage();
      y = drawPageHeader(doc, {
        margin,
        pageW,
        nombreTorneo,
        fechaTorneo,
        subtitle: 'Llave eliminatoria — Tabla',
        isFirstPage: false,
        extraRight: torneoFinalizado ? 'Estado: finalizado' : 'Estado: en curso',
      });
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text(rondaLabels[ronda] || RONDA_LABELS_DEFAULT[ronda] || ronda, margin, y + 4);
    y += 6;

    const partidos = (flat[ronda] || [])
      .slice()
      .sort((a, b) => (a.tpl_orden ?? 0) - (b.tpl_orden ?? 0));

    autoTable(doc, {
      startY: y,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 1.6,
        overflow: 'linebreak',
        valign: 'middle',
        textColor: [33, 33, 33],
        lineColor: [220, 220, 220],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: HEAD_COLOR,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center',
      },
      margin: { left: margin, right: margin, bottom: footerReserve },
      head: [['#', 'Pareja A', 'Pareja B', 'Resultado', 'Ganador']],
      body: partidos.map((p) => [
        String(p.tpl_orden ?? ''),
        [p.pareja_a || TBD, p.clasificacion_a ? `[${p.clasificacion_a}]` : ''].filter(Boolean).join('  '),
        [p.pareja_b || TBD, p.clasificacion_b ? `[${p.clasificacion_b}]` : ''].filter(Boolean).join('  '),
        formatearResultadoLlave(p),
        p.ganador || '-',
      ]),
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 'auto', halign: 'left' },
        2: { cellWidth: 'auto', halign: 'left' },
        3: { cellWidth: 38, halign: 'center' },
        4: { cellWidth: 42, halign: 'left' },
      },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (data.column.index === 3 && data.cell.raw === 'Pendiente') {
          data.cell.styles.textColor = [160, 160, 160];
          data.cell.styles.fontStyle = 'italic';
        }
        if (data.row.index % 2 === 1) {
          data.cell.styles.fillColor = ALT_FILL;
        }
      },
    });

    y = (doc.lastAutoTable?.finalY ?? y) + (idx < rounds.length - 1 ? 10 : 6);
  });

  addFooters(doc, margin, dayjs().format('DD/MM/YYYY HH:mm'));
  doc.save(`llave-tabla-${slugify(nombreTorneo)}-${dayjs().format('YYYY-MM-DD')}.pdf`);
};

export const exportarLlavePdf = ({
  torneo,
  porRonda,
  modo = 'bracket',
  rondaLabels = RONDA_LABELS_DEFAULT,
  torneoFinalizado = false,
}) => {
  const payload = { torneo, porRonda, rondaLabels, torneoFinalizado };
  if (modo === 'table') {
    exportarLlaveTablaPdf(payload);
    return;
  }
  exportarLlaveArbolPdf(payload);
};
